/*
 * Collection filter drawer (snippets/collection-filter-drawer.liquid).
 *
 * The form is a draft: changing it fetches this section for the draft URL (Section
 * Rendering API) to refresh the live counts and "Show N styles", without touching the
 * grid. "Show N styles" writes the draft into #filters-params and asks the section's
 * <facets-filter> to re-render, which updates the grid, count, badge, chips, toolbar
 * sort and URL. Closing any other way restores the form as it was when opened.
 */
(function () {
  const DRAWER_SCRIPT_VERSION = 6;
  if (window.__collectionFilterDrawerVersion >= DRAWER_SCRIPT_VERSION) return;
  window.__collectionFilterDrawerVersion = DRAWER_SCRIPT_VERSION;

  // --- URL rules (pure; exercised by scripts/collection-grid tests) ---

  /**
   * The query the draft stands for. Keys the drawer manages are rebuilt from the form;
   * anything else in the applied URL (e.g. a search term) is kept. Paging always resets,
   * and the default sort is left out so defaults keep a clean URL.
   *
   * @param {string} appliedQuery current applied query string
   * @param {{
   *   categories?: Array<{ params: string[], param: string, value: string }>,
   *   lists: Array<{ param: string, values: string[] }>,
   *   prices: Array<{ minParam: string, maxParam: string, min: string, max: string }>,
   *   sort: string,
   *   defaultSort: string,
   * }} draft
   */
  const buildDraftQuery = (appliedQuery, draft) => {
    const params = new URLSearchParams(appliedQuery);
    const managed = new Set(['sort_by', 'page', 'section_id']);
    const categories = draft.categories || [];
    categories.forEach(({ params: categoryParams }) => categoryParams.forEach((key) => managed.add(key)));
    draft.lists.forEach(({ param }) => managed.add(param));
    draft.prices.forEach(({ minParam, maxParam }) => {
      managed.add(minParam);
      managed.add(maxParam);
    });
    [...params.keys()].filter((key) => managed.has(key)).forEach((key) => params.delete(key));

    categories.forEach(({ param, value }) => {
      if (param && value) params.set(param, value);
    });
    draft.lists.forEach(({ param, values }) => values.forEach((value) => params.append(param, value)));
    draft.prices.forEach(({ minParam, maxParam, min, max }) => {
      if (min) params.set(minParam, min);
      if (max) params.set(maxParam, max);
    });
    if (draft.sort && draft.sort !== draft.defaultSort) params.set('sort_by', draft.sort);

    return params.toString();
  };

  /** "Show 1 style" / "Show N styles". */
  const applyLabel = (count, singular = 'style', plural = 'styles') => {
    const n = Math.max(0, Math.trunc(Number(count) || 0));
    return `Show ${n} ${n === 1 ? singular : plural}`;
  };

  window.CollectionFilterDrawer = { buildDraftQuery, applyLabel };

  if (typeof document === 'undefined' || !document.querySelector) return;

  // --- Drawer behavior ---

  const FOCUSABLE =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const LOCK_CLASS = 'collection-filter-drawer-open';
  const COUNT_DELAY_MS = 250;

  let openerId = null;
  let openSnapshot = null;
  let countTimer = 0;
  let countController = null;
  let closeTimer = 0;
  let refocusOpener = false;

  const drawer = () => document.querySelector('[data-filter-drawer]');
  const panel = () => drawer()?.querySelector('[data-filter-drawer-panel]');
  const form = () => drawer()?.querySelector('[data-filter-drawer-form]');
  const isOpen = () => drawer()?.classList.contains('is-open') === true;

  const appliedQuery = () => {
    const input = document.getElementById('filters-params');
    if (!input) return window.location.search.replace(/^\?/, '');
    return new URL(input.value, window.location.origin).search.replace(/^\?/, '');
  };

  const readDraft = () => {
    const root = drawer();
    const draft = { categories: [], lists: [], prices: [], sort: '', defaultSort: root?.dataset.defaultSort || '' };
    form()
      ?.querySelectorAll('[data-filter-group]')
      .forEach((group) => {
        const inputs = [...group.querySelectorAll('[data-filter-input]')];
        if (group.dataset.filterGroup === 'category') {
          // Shop for: one radio across the category buttons' filters (they can differ).
          const picked = inputs.find((input) => input.checked);
          draft.categories.push({
            params: (group.dataset.params || '').split('|').filter(Boolean),
            param: picked?.dataset.param || '',
            value: picked?.value || '',
          });
        } else if (group.dataset.filterGroup === 'price') {
          const picked = inputs.find((input) => input.checked);
          draft.prices.push({
            minParam: group.dataset.minParam,
            maxParam: group.dataset.maxParam,
            min: picked?.dataset.min || '',
            max: picked?.dataset.max || '',
          });
        } else {
          draft.lists.push({
            param: group.dataset.param,
            values: inputs.filter((input) => input.checked && input.value).map((input) => input.value),
          });
        }
      });
    draft.sort = form()?.querySelector('[data-filter-sort]')?.value || '';
    return draft;
  };

  const setCount = (count, { announce = false } = {}) => {
    const root = drawer();
    if (!root) return;
    const label = applyLabel(count, root.dataset.nounSingular, root.dataset.nounPlural);
    const apply = root.querySelector('[data-filter-drawer-apply]');
    if (apply) apply.textContent = label;
    const status = root.querySelector('[data-filter-drawer-status]');
    if (status && announce) status.textContent = `${label.replace(/^Show /, '')} match`;
  };

  /** Counts for the draft come from Shopify: render this section for the draft URL. */
  const refreshCounts = async () => {
    const root = drawer();
    if (!root) return;
    countController?.abort();
    countController = new AbortController();
    const query = buildDraftQuery(appliedQuery(), readDraft());
    const url = `${root.dataset.baseUrl}?${query}${query ? '&' : ''}section_id=${root.dataset.sectionId}`;
    try {
      const response = await fetch(url, { signal: countController.signal });
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('[data-render-filter-drawer]');
      if (!fresh || !isOpen()) return;
      setCount(fresh.dataset.filterDrawerTotal, { announce: true });
      const current = new Map(
        [...root.querySelectorAll('[data-count-key]')].map((el) => [el.dataset.countKey, el])
      );
      fresh.querySelectorAll('[data-count-key]').forEach((el) => {
        const target = current.get(el.dataset.countKey);
        if (target) target.textContent = el.textContent;
      });
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Filter drawer counts failed', error);
    }
  };

  const scheduleCounts = () => {
    window.clearTimeout(countTimer);
    countTimer = window.setTimeout(refreshCounts, COUNT_DELAY_MS);
  };

  const syncOpeners = () => {
    document.querySelectorAll('[data-filter-drawer-open]').forEach((button) => {
      button.setAttribute('aria-expanded', isOpen() ? 'true' : 'false');
    });
  };

  const openDrawer = (opener) => {
    const root = drawer();
    if (!root) return;
    window.clearTimeout(closeTimer);
    openerId = opener?.id || null;
    openSnapshot = {
      form: form()?.innerHTML ?? null,
      apply: root.querySelector('[data-filter-drawer-apply]')?.textContent ?? '',
    };
    root.hidden = false;
    document.documentElement.classList.add(LOCK_CLASS);
    // Next frame, so the slide-in transition runs from the hidden state.
    window.requestAnimationFrame(() => {
      root.classList.add('is-open');
      syncOpeners();
      root.querySelector('.collection-filter-drawer__close')?.focus();
    });
  };

  /** Closes; unless the draft was just applied, the form goes back to how it was opened. */
  const closeDrawer = ({ applied = false } = {}) => {
    const root = drawer();
    if (!root || !isOpen()) return;
    window.clearTimeout(countTimer);
    countController?.abort();
    if (!applied && openSnapshot) {
      const body = form();
      if (body && openSnapshot.form !== null) body.innerHTML = openSnapshot.form;
      const apply = root.querySelector('[data-filter-drawer-apply]');
      if (apply) apply.textContent = openSnapshot.apply;
    }
    openSnapshot = null;
    root.classList.remove('is-open');
    document.documentElement.classList.remove(LOCK_CLASS);
    syncOpeners();
    closeTimer = window.setTimeout(() => {
      root.hidden = true;
    }, 300);
    const opener = openerId && document.getElementById(openerId);
    (opener || document.querySelector('[data-filter-drawer-open]'))?.focus();
  };

  const resetDraft = () => {
    const root = drawer();
    form()
      ?.querySelectorAll('[data-filter-group]')
      .forEach((group) => {
        const inputs = [...group.querySelectorAll('[data-filter-input]')];
        inputs.forEach((input) => {
          input.checked = input.type === 'radio' && input.value === '';
        });
      });
    const sort = form()?.querySelector('[data-filter-sort]');
    if (sort) sort.value = root?.dataset.defaultSort || sort.options[0]?.value || '';
    scheduleCounts();
  };

  const applyDraft = () => {
    const root = drawer();
    const input = document.getElementById('filters-params');
    const facets = document.querySelector('facets-filter');
    if (!root || !input || !facets) return;
    const query = buildDraftQuery(appliedQuery(), readDraft());
    const url = new URL(input.value, window.location.origin);
    url.search = query;
    input.value = url.toString();
    closeDrawer({ applied: true });
    // The re-render replaces the Filters button; focus moves to the new one afterwards.
    refocusOpener = true;
    facets.onSubmitHandler();
  };

  const trapFocus = (event) => {
    const container = panel();
    if (!container) return;
    const focusable = [...container.querySelectorAll(FOCUSABLE)].filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-filter-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener);
      return;
    }
    if (!isOpen()) return;
    if (event.target.closest('[data-filter-drawer-close]')) closeDrawer();
    else if (event.target.closest('[data-filter-drawer-reset]')) resetDraft();
    else if (event.target.closest('[data-filter-drawer-apply]')) applyDraft();
  });

  /**
   * Same rule as the toolbar category buttons: picking a different Shop for option clears
   * the draft's "filters to clear" (Style by default), keeping price and sort. The params
   * come from the category row, which lists them in data-clear-params.
   */
  const clearForCategoryPick = (input) => {
    if (!input.closest('[data-filter-group="category"]')) return;
    const clearParams = (document.querySelector('[data-collection-category-nav]')?.dataset.clearParams || '')
      .split('|')
      .filter(Boolean);
    if (!clearParams.length) return;
    form()
      ?.querySelectorAll('[data-filter-group="list"]')
      .forEach((group) => {
        if (!clearParams.includes(group.dataset.param)) return;
        group.querySelectorAll('[data-filter-input]').forEach((option) => {
          // One-option groups fall back to their "All" row; checkboxes are simply cleared.
          option.checked = option.type === 'radio' && option.value === '';
        });
      });
  };

  document.addEventListener('change', (event) => {
    if (!isOpen() || !event.target.closest('[data-filter-drawer-form]')) return;
    clearForCategoryPick(event.target);
    scheduleCounts();
  });

  document.addEventListener('submit', (event) => {
    if (event.target.closest('[data-filter-drawer-form]')) {
      event.preventDefault();
      applyDraft();
    }
  });

  /**
   * Radio groups (Shop for, Starting price) take one Tab stop by default, with arrow keys
   * between options. Tab / Shift+Tab also step through each option here, moving focus
   * without selecting (Space or Enter picks), so every option is reachable with Tab.
   */
  const stepThroughRadioGroup = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return false;
    const group = radio.closest('[data-filter-group]');
    if (!group) return false;
    const radios = [...group.querySelectorAll('input[type="radio"]:not([disabled])')];
    const next = radios[radios.indexOf(radio) + (event.shiftKey ? -1 : 1)];
    if (!next) return false;
    event.preventDefault();
    next.focus();
    return true;
  };

  document.addEventListener('keydown', (event) => {
    if (!isOpen()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
    } else if (event.key === 'Tab') {
      if (!stepThroughRadioGroup(event)) trapFocus(event);
    } else if (event.key === 'Enter' && event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      // Enter picks a focused option too (Space already does), without submitting the form.
      event.preventDefault();
      if (!event.target.checked) {
        event.target.checked = true;
        event.target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  /** Called by the section after each re-render (facets-filter renderFetch / renderFromCache). */
  window.initCollectionFilterDrawer = () => {
    syncOpeners();
    const root = drawer();
    const total = root?.querySelector('[data-render-filter-drawer]')?.dataset.filterDrawerTotal;
    if (root && total !== undefined) setCount(total);
    if (refocusOpener) {
      refocusOpener = false;
      const opener = (openerId && document.getElementById(openerId)) || document.querySelector('[data-filter-drawer-open]');
      // No scroll: applying already took the shopper to the top of the page.
      opener?.focus({ preventScroll: true });
    }
  };
})();
